// Tokens and geometry for the Figma frame "Friends / Mobile — Modal" (4244:41053) - page
// "PAVEL DRAFT". The friends artboard is 390x957 where the inventory one is 390x844, so its
// "Modal / Friends" panel is drawn taller (358x853 at 16/44) than "Modal / Inventory"
// (358x690 at 16/77). Both draw the identical header, and the app reaches Friends by switching a
// tab inside the panel that is already open - resizing the panel mid-switch is not something the
// design asks for - so the panel keeps the inventory geometry and only the body changes. Every
// number below is in the shared 390-wide design space and is scaled once by the screen.
//
// The colours are the file's own variables, read with get_variable_defs off the modal; the
// gradients are the literal values its design context paints.
import { INVENTORY_FONT, inventoryColors } from '../Inventory/inventoryTheme';

export const FRIENDS_FONT = INVENTORY_FONT;

export const friendsColors = {
	/** surface/raised - the invite card, the search input and every friend row. */
	surface: inventoryColors.surface,
	/** surface/sunken - the two action buttons on a row. */
	surfaceSunken: '#0F0301',
	/** The same duller orange hairline the inventory cells carry. */
	border: inventoryColors.itemBorder,
	/** border/gold - the action buttons' outline. */
	borderGold: '#E89200',
	/** gold/50 - a friend's username. */
	gold50: inventoryColors.gold50,
	/** gold/300 - the level badge disc. */
	gold300: '#FFC500',
	/** text/on-gold - the level number, and the cross on the remove-friend glyph. */
	onGold: '#2A0A04',
	/** text/accent - the invite card's title and the friends counter. */
	accent: inventoryColors.accent,
	/** text/muted - the invite card's subtitle, the placeholder, an offline status. */
	muted: inventoryColors.muted,
	/** text/primary - the invite link and other plain labels. */
	primary: inventoryColors.primary,
	/**
	 * progress-bar-level-green - an online status.
	 *
	 * The artboard is not consistent here: nine of the ten rows read this green, and the second
	 * (LuckyCharm) reads `state/success/base` #0CEB66 instead. That is a slip in the mock data, not
	 * two states, so one status maps to one colour.
	 */
	online: '#4ABF54',
	/** gold/200 - an "In Game" status label; its dot is the gradient below. */
	inGame: '#FFD766',
	/** An offline status is text/muted, dot included. */
	offline: inventoryColors.muted,
} as const;

/** The "In Game" dot is the only one the design paints as a gradient rather than a flat fill. */
export const IN_GAME_DOT_FILL: Array<[string, string]> = [
	['0', '#FCF17B'],
	['1', '#E19010'],
];
/** radius/md for the buttons and the input, radius/lg for the cards. */
export const RADIUS_MD = 8;
export const RADIUS_LG = 12;

/** Every card, button and field in this frame carries the same 1 stroke. */
export const hairline = (scale: number) => Math.max(1, 1 * scale);

/**
 * A Figma frame's width and height include its stroke, and it lays its children out from that
 * outer edge: the invite card is 124 tall *with* the hairline, and the avatar sits 10 from the row's
 * outer edge, not 10 from inside its border. React Native puts the border outside the padding box
 * instead, so a padding taken straight from the file makes the frame 2 too big and pushes its
 * contents 1 in. Every padding the design measures from an outer edge goes through this.
 */
export const inset = (padding: number, scale: number) =>
	Math.max(0, padding * scale - hairline(scale));

/** "Body" (4244:41082) starts under the 68-tall header and its content insets 12 on every side. */
export const BODY_TOP = 68;
export const BODY_PADDING = 12;
/** space/12 between the invite card, the search row and the list. */
export const SECTION_GAP = 12;
/** The scroll indicator runs down the right edge of the body, 8 clear of either end. */
export const SCROLL_BAR = { x: 350, inset: 8 };
